/*----------------------------------
- COMPARE PAGES REGISTRY (SSOT)
----------------------------------*/

// Cross-runtime identity registry for the public /compare pages.
// Page copy lives in /client/catalogs/landing/compare; server surfaces
// (sitemap, AI markdown mirrors) and client routes both key off this file.

/*----------------------------------
- TYPES
----------------------------------*/

export type ComparePageCategory =
    | 'one_word_databases'
    | 'brandable_marketplaces'
    | 'investor_data_tools'
    | 'search_and_drop_utilities';

export type ComparePageRelationship = 'replace' | 'pair' | 'both';

export type ComparePageRegistryEntry = {
    slug: string;
    competitorName: string;
    /** Short name used in running copy and FAQ questions. */
    competitorShortName: string;
    /** Optional compact label for shared footer navigation. */
    footerLabel?: string;
    /** Whether this comparison is part of the curated shared footer navigation. */
    footerFeatured?: boolean;
    competitorUrl: string;
    category: ComparePageCategory;
    pageTitle: string;
    metaDescription: string;
    heroHeadline: string;
    /**
     * One line on the hub card. The verdict line is written for the page it opens
     * and runs two or three clauses, which is a paragraph inside a 3-up card, so the
     * hub says the same thing in one sentence instead.
     */
    hubCardLine: string;
    verdictLine: string;
    relationship: ComparePageRelationship;
    /** Sibling compare slugs shown in the "Other comparisons" band, by category affinity. */
    siblingSlugs: readonly string[];
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

export const COMPARE_BASE_PATH = '/compare';

/** Date every competitor fact on the compare pages was last verified. Rendered on-page. */
export const COMPARE_FACTS_CHECKED_ON = '2026-07-15';

export const COMPARE_CATEGORY_LABELS: Record<ComparePageCategory, string> = {
    one_word_databases: 'One-word lists and databases',
    brandable_marketplaces: 'Brandable marketplaces',
    investor_data_tools: 'Investor data and appraisal tools',
    search_and_drop_utilities: 'Search and drop utilities',
};

/** Compact group headings for the Resources menu, where the full labels wrap. */
export const COMPARE_CATEGORY_MENU_LABELS: Record<ComparePageCategory, string> = {
    one_word_databases: 'One-word lists & databases',
    brandable_marketplaces: 'Brandable marketplaces',
    investor_data_tools: 'Investor data & appraisal',
    search_and_drop_utilities: 'Search & drop utilities',
};

/** Menu group order, and the only order the grouped export is allowed to use. */
export const COMPARE_CATEGORY_ORDER: readonly ComparePageCategory[] = [
    'one_word_databases',
    'brandable_marketplaces',
    'investor_data_tools',
    'search_and_drop_utilities',
];

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

export const COMPARE_PAGE_REGISTRY: readonly ComparePageRegistryEntry[] = [
    {
        slug: 'one-word-domains',
        competitorName: 'oneword.domains',
        competitorShortName: 'oneword.domains',
        footerFeatured: true,
        competitorUrl: 'https://oneword.domains/',
        category: 'one_word_databases',
        pageTitle: 'oneword.domains Alternative: Unique Domains vs oneword.domains',
        metaDescription:
            'Looking for a oneword.domains alternative? Compare the one word domains database with Unique Domains: search, vetting, tracking, pricing and the lifetime deal.',
        heroHeadline: 'Unique Domains vs oneword.domains',
        hubCardLine: 'The list, or the decision that starts where the list ends.',
        verdictLine:
            'oneword.domains is a genuinely good browsing database at a fair lifetime price; Unique Domains is the decision layer, so choose by whether your job ends at the list or at a registered name.',
        relationship: 'both',
        siblingSlugs: ['mononames', 'expireddomains-net', 'instant-domain-search'],
    },
    {
        slug: 'atom',
        competitorName: 'Atom.com (formerly Squadhelp)',
        competitorShortName: 'Atom.com',
        competitorUrl: 'https://www.atom.com/',
        category: 'brandable_marketplaces',
        pageTitle: 'Atom.com vs Unique Domains: A Squadhelp Alternative Compared',
        metaDescription:
            'Looking for a Squadhelp or Atom.com alternative? Compare Atom.com with Unique Domains on one-word domain search, neutral evaluation, pricing and who each fits.',
        heroHeadline: 'Unique Domains vs Atom.com',
        hubCardLine: 'The shelf of invented brands against the one-word market.',
        verdictLine:
            'Atom is a strong place to buy a brandable name and a fair place to sell one; Unique Domains is where you decide, because the platform scoring the name should not be the one selling it.',
        relationship: 'both',
        siblingSlugs: ['brandbucket', 'one-word-domains', 'instant-domain-search'],
    },
    {
        slug: 'bishopi',
        competitorName: 'Bishopi',
        competitorShortName: 'Bishopi',
        competitorUrl: 'https://bishopi.io/',
        category: 'investor_data_tools',
        pageTitle: 'Bishopi Alternative: Unique Domains vs Bishopi Compared',
        metaDescription:
            'Need a Bishopi alternative for one-word domains? Compare Bishopi and Unique Domains on data coverage, decision workflow, pricing and which one fits your work.',
        heroHeadline: 'Unique Domains vs Bishopi',
        hubCardLine: 'Investor data feeds against decision reports.',
        verdictLine:
            'Choose Bishopi for all-purpose domain and SEO measurement, choose Unique Domains when the decision is a one-word name, and pair them if you work both.',
        relationship: 'both',
        siblingSlugs: ['estibot', 'namebio', 'dotdb'],
    },
    {
        slug: 'mononames',
        competitorName: 'mononames',
        competitorShortName: 'mononames',
        footerFeatured: true,
        competitorUrl: 'https://mononames.com/',
        category: 'one_word_databases',
        pageTitle: 'Unique Domains vs mononames (2026): Lists or a Decision Workflow?',
        metaDescription:
            'Compare Unique Domains and mononames: the largest one-word availability lists vs an evaluation and monitoring workflow, with lifetime-pricing concessions.',
        heroHeadline: 'Unique Domains vs mononames',
        hubCardLine: 'A hand-picked list of one-word names against evaluation and follow-up.',
        verdictLine:
            'Buy mononames if you want the biggest one-off list of hand-registerable one-word domains; use Unique Domains when you need to evaluate, monitor, and decide instead of scroll.',
        relationship: 'both',
        siblingSlugs: ['one-word-domains', 'expireddomains-net', 'dotdb'],
    },
    {
        slug: 'dotdb',
        competitorName: 'dotDB',
        competitorShortName: 'dotDB',
        competitorUrl: 'https://dotdb.com/',
        category: 'investor_data_tools',
        pageTitle: 'Unique Domains vs dotDB: Honest Comparison for 2026',
        metaDescription:
            'Compare Unique Domains and dotDB: keyword registration counts vs a one-word decision workflow, where each tool wins, and how investors pair them.',
        heroHeadline: 'Unique Domains vs dotDB',
        hubCardLine: 'TLD coverage counts against a full case per name.',
        verdictLine:
            'Keep dotDB if raw registration counts drive your process; add Unique Domains when the shortlist needs to become a buy, pass, or watch decision.',
        relationship: 'both',
        siblingSlugs: ['namebio', 'estibot', 'bishopi'],
    },
    {
        slug: 'namebio',
        competitorName: 'NameBio',
        competitorShortName: 'NameBio',
        competitorUrl: 'https://namebio.com/',
        category: 'investor_data_tools',
        pageTitle: 'Unique Domains vs NameBio: Sold Prices vs Live Domain Decisions',
        metaDescription:
            'Compare Unique Domains and NameBio: the sold-price archive vs a live one-word decision workflow, and why most investors run both.',
        heroHeadline: 'NameBio tells you what sold. Unique Domains helps you decide what to buy.',
        hubCardLine: 'NameBio tells you what sold. Unique Domains helps you decide what to buy.',
        verdictLine:
            'Keep NameBio as your sold-price archive, add Unique Domains when the question shifts from what sold to what should I buy now.',
        relationship: 'pair',
        siblingSlugs: ['estibot', 'dotdb', 'bishopi'],
    },
    {
        slug: 'estibot',
        competitorName: 'Estibot',
        competitorShortName: 'Estibot',
        competitorUrl: 'https://www.estibot.com/',
        category: 'investor_data_tools',
        pageTitle: 'Unique Domains vs Estibot: A Price Is Not a Decision (2026)',
        metaDescription:
            'Compare Unique Domains and Estibot: the industry-default appraisal number vs a Fit, Ownership, Risk decision workflow for one-word domains.',
        heroHeadline: 'Unique Domains vs Estibot: the number, or the decision',
        hubCardLine: 'The number, or the decision.',
        verdictLine:
            'Keep Estibot when you need a widely recognized price number at scale; choose Unique Domains when the real question is whether a one-word domain is worth acquiring at all.',
        relationship: 'both',
        siblingSlugs: ['namebio', 'dotdb', 'bishopi'],
    },
    {
        slug: 'instant-domain-search',
        competitorName: 'Instant Domain Search',
        competitorShortName: 'Instant Domain Search',
        footerLabel: 'instant.ai',
        footerFeatured: true,
        competitorUrl: 'https://instantdomainsearch.com/',
        category: 'search_and_drop_utilities',
        pageTitle: 'Unique Domains vs Instant Domain Search: 2026 Comparison',
        metaDescription:
            'Compare Unique Domains and Instant Domain Search: free availability checks vs a paid one-word decision workflow, and why the two pair well.',
        heroHeadline: 'Availability is a fact. The right domain is a decision.',
        hubCardLine: 'Availability is a fact. The right domain is a decision.',
        verdictLine:
            'Keep Instant Domain Search for millisecond availability checks; bring in Unique Domains when a one-word domain is real money and the decision has to be right.',
        relationship: 'both',
        siblingSlugs: ['one-word-domains', 'atom', 'mononames'],
    },
    {
        slug: 'expireddomains-net',
        competitorName: 'ExpiredDomains.net',
        competitorShortName: 'ExpiredDomains.net',
        competitorUrl: 'https://www.expireddomains.net/',
        category: 'search_and_drop_utilities',
        pageTitle: 'Unique Domains vs ExpiredDomains.net: Drop Lists vs Decisions',
        metaDescription:
            'Compare Unique Domains and ExpiredDomains.net: the free drop-list firehose vs an evaluation layer that turns one-word candidates into decisions.',
        heroHeadline: 'Unique Domains vs ExpiredDomains.net',
        hubCardLine: 'The drop-list firehose against a screener with the facts shown.',
        verdictLine:
            'Keep ExpiredDomains.net for free raw drop coverage; add Unique Domains when a one-word name needs a decision instead of another tab of filters.',
        relationship: 'both',
        siblingSlugs: ['one-word-domains', 'mononames', 'namebio'],
    },
    {
        slug: 'brandbucket',
        competitorName: 'BrandBucket',
        competitorShortName: 'BrandBucket',
        competitorUrl: 'https://www.brandbucket.com/',
        category: 'brandable_marketplaces',
        pageTitle: 'Unique Domains vs BrandBucket: Which to Use in 2026?',
        metaDescription:
            'Compare Unique Domains and BrandBucket: whole-market one-word evaluation vs a curated brandable marketplace, with verified commissions and pricing.',
        heroHeadline: 'Unique Domains vs BrandBucket: the market, not just the shelf',
        hubCardLine: 'The market, not just the shelf.',
        verdictLine:
            'Buy from BrandBucket when its shelf has your name; use Unique Domains to know whether it does, what it should cost, and what else exists.',
        relationship: 'both',
        siblingSlugs: ['atom', 'one-word-domains', 'namebio'],
    },
] as const;

/*----------------------------------
- HELPERS
----------------------------------*/

export const comparePagePath = (slug: string): string => `${COMPARE_BASE_PATH}/${slug}`;

export const getComparePageEntry = (slug: string): ComparePageRegistryEntry | undefined =>
    COMPARE_PAGE_REGISTRY.find((entry) => entry.slug === slug);

export const COMPARE_PAGE_SLUGS: readonly string[] = COMPARE_PAGE_REGISTRY.map((entry) => entry.slug);

export const FOOTER_COMPARE_PAGE_REGISTRY: readonly ComparePageRegistryEntry[] = COMPARE_PAGE_REGISTRY.filter(
    (entry) => entry.footerFeatured,
);
